import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Store, Briefcase, Users, UserSearch, Sparkles, Check, ChevronDown, ExternalLink, Loader2, Trash2, Megaphone } from "lucide-react";
import AdvertiseDialog, { VendorAdsList } from "@/components/vendor/AdvertiseDialog";
import { useApp } from "@/context/AppContext";
import { fetchMyActivities, saveMyActivities, saveActivityProfile, deleteProfessionalListing, formatApiError } from "@/lib/api";
import { ActivityForm, errorIn } from "@/components/activities/ActivityForm";
import JobsManager from "@/components/activities/JobsManager";
import { ReceivedLocations } from "@/components/maps/LocationShare";
import { SendLocationToCustomer } from "@/components/maps/SendToCustomer";

export const ACTIVITY_META = [
  { id: "shop", label: "Shop / Product Business", icon: Store },
  { id: "professional", label: "Services / Professional", icon: Briefcase },
  { id: "employer", label: "Employer / Hiring", icon: Users },
  { id: "job_seeker", label: "Job Seeker", icon: UserSearch },
  { id: "other", label: "Other / Custom Activity", icon: Sparkles },
];

function ShopSection({ state }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (state.roles.includes("vendor")) {
    return <div className="text-sm text-slate-600">{t("ob.shopActive")} <button onClick={() => navigate("/vendor")} className="text-emerald-700 font-semibold" data-testid="shop-open-dashboard">{t("ob.openDash")}</button></div>;
  }
  return (
    <div className="text-sm text-slate-600">
      {t("ob.shopSetup")}{" "}
      <button onClick={() => navigate("/profile?setup=1")} className="text-emerald-700 font-semibold" data-testid="shop-start-setup">{t("ob.startShop")}</button>
    </div>
  );
}

function Pct({ value }) {
  const { t } = useTranslation();
  const cls = value >= 100 ? "bg-emerald-100 text-emerald-800" : value > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600";
  return <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${cls}`}>{value >= 100 ? t("ob.complete") : t("ob.pctDone", { pct: value })}</span>;
}

export default function Onboarding() {
  const { user, authChecking } = useApp();
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [picked, setPicked] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [autoPost] = useState(() => new URLSearchParams(window.location.search).get("post") === "1");
  const [adOpen, setAdOpen] = useState(false);
  const [adsKey, setAdsKey] = useState(0);
  const [editJobId] = useState(() => new URLSearchParams(window.location.search).get("edit") || "");

  useEffect(() => {
    if (!user) return;
    const wantEmployer = new URLSearchParams(window.location.search).get("open") === "employer";
    fetchMyActivities().then(async (s) => {
      if (wantEmployer && !s.activities.includes("employer")) s = await saveMyActivities([...s.activities, "employer"]);
      setState(s); setPicked(s.activities); setOpen(wantEmployer ? "employer" : s.activities[0] || null);
      if (wantEmployer) window.history.replaceState(window.history.state, "", window.location.pathname);
      if (wantEmployer) setTimeout(() => document.querySelector('[data-testid="jobs-manager"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user && !authChecking) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center" data-testid="onboarding-login">
        <p className="text-slate-600 mb-3">{t("ob.loginNeeded")}</p>
        <button onClick={() => window.dispatchEvent(new Event("vb:open-auth"))} className="rounded-full bg-emerald-700 text-white px-5 py-2 text-sm font-semibold">Login / Sign up</button>
      </div>
    );
  }
  if (!state) return <div className="py-16 text-center text-slate-500">Loading...</div>;

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const saveChoices = async () => {
    setBusy("choices");
    try { const s = await saveMyActivities(picked); setState(s); setOpen(picked[0] || null); toast.success(t("ob.choicesSaved")); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const deleteListing = async () => {
    if (!window.confirm("Apni professional service listing permanently delete karein? Yeh public listings aur search se hat jayegi. Profile ki baaki jaankari aur reviews surakshit rahenge.")) return;
    setBusy("professional");
    try {
      await deleteProfessionalListing();
      setState(await fetchMyActivities());
      setFormKey((k) => k + 1);
      toast.success("Service listing delete ho gayi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const saveProfile = async (kind, data, li = 0) => {
    setBusy(kind);
    try {
      const s = await saveActivityProfile(kind, data);
      setState(s);
      const pending = kind === "professional" && data.published && s.professional_listing?.review_status === "pending";
      toast.success(pending ? t("ob.savedPending") : t("ob.saved"));
    } catch (e) { toast.error(errorIn(formatApiError(e), li)); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="onboarding-page">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{t("ob.title")}</h1>
      <p className="text-sm text-slate-500 mt-1">{t("ob.sub")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5">
        {ACTIVITY_META.map(({ id, label, icon: Icon }) => {
          const on = picked.includes(id);
          return (
            <button key={id} onClick={() => toggle(id)} aria-pressed={on} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${on ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300 bg-white"}`} data-testid={`activity-choice-${id}`}>
              <Icon className={`w-5 h-5 flex-shrink-0 ${on ? "text-emerald-700" : "text-amber-600"}`} />
              <span className="flex-1 text-sm font-bold text-slate-900">{t(`act.${id}`, label)}</span>
              {on && <Check className="w-4 h-4 text-emerald-700" />}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button onClick={saveChoices} disabled={busy === "choices"} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm font-semibold disabled:opacity-60" data-testid="activity-choices-save">
          {busy === "choices" ? <Loader2 className="w-4 h-4 animate-spin" /> : t("ob.saveChoices")}
        </button>
        <Link to="/" className="text-sm text-slate-500 hover:text-emerald-700" data-testid="onboarding-skip">{t("ob.skip")}</Link>
        <Link to={state.public_url} className="ms-auto inline-flex items-center gap-1 text-sm text-emerald-700 font-semibold" data-testid="onboarding-public-link">{t("ob.publicProfile")} <ExternalLink className="w-3.5 h-3.5" /></Link>
      </div>

      <div className="mt-6 space-y-3">
        {ACTIVITY_META.filter((m) => state.activities.includes(m.id)).map(({ id, label, icon: Icon }) => (
          <div key={id} className="bg-white border border-slate-100 rounded-2xl" data-testid={`activity-section-${id}`}>
            <button onClick={() => setOpen(open === id ? null : id)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`activity-section-toggle-${id}`}>
              <Icon className="w-5 h-5 text-emerald-700" />
              <span className="flex-1 font-bold text-slate-900">{t(`act.${id}`, label)}</span>
              <Pct value={state.completion[id]} />
              {state.profiles[id]?.published && <span className="text-[11px] font-bold rounded-full px-2 py-0.5 bg-sky-100 text-sky-800">{t("ob.public")}</span>}
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open === id ? "rotate-180" : ""}`} />
            </button>
            {open === id && (
              <div className="px-4 pb-4">
                {id === "employer" && <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1" data-testid="employer-details-heading">Employer / organisation details</h3>}
                {id === "shop" ? <ShopSection state={state} /> : <ActivityForm key={`${id}-${formKey}`} kind={id} initial={state.profiles[id]} busy={busy === id} onSave={(d, li) => saveProfile(id, d, li)} />}
                {id === "professional" && state.professional_listing && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
                    <div className="text-xs text-slate-500" data-testid="professional-listing-status">{t("ob.listing")}: {state.professional_listing.review_status === "approved" ? (state.professional_listing.active ? t("ob.listingLive") : t("ob.listingHidden")) : state.professional_listing.review_status === "pending" ? t("ob.listingPending") : t("ob.listingRejected")}</div>
                    {state.professional_listing.review_status === "approved" && state.professional_listing.active && <button onClick={() => setAdOpen(true)} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white px-3 py-1.5 text-xs font-semibold min-h-[36px]" data-testid="professional-advertise-btn"><Megaphone className="w-3.5 h-3.5" /> Advertise</button>}
                    <button onClick={deleteListing} disabled={busy === "professional"} className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 min-h-[36px] disabled:opacity-60" data-testid="professional-listing-delete"><Trash2 className="w-3.5 h-3.5" /> Delete listing</button>
                  </div>
                )}
                {id === "professional" && state.professional_listing && (
                  <>
                    <AdvertiseDialog open={adOpen} onOpenChange={(o) => { setAdOpen(o); if (!o) setAdsKey((k) => k + 1); }} target={{ type: "service", id: `pro-${user.id}`, name: state.profiles.professional?.profession || "Professional service" }} />
                    <div className="mt-3" data-testid="professional-ads-list"><VendorAdsList refreshKey={adsKey} /></div>
                    <div className="mt-3"><ReceivedLocations><SendLocationToCustomer /></ReceivedLocations></div>
                  </>
                )}
                {id === "employer" && <JobsManager autoOpen={autoPost} editId={editJobId} />}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
